import { Auth } from "aws-amplify";
import { takeEvery, call, put, fork, all, } from "redux-saga/effects";

import * as actions from "../../actions";
import { types, updateUserProfilePayload, updateUserNotificationPrefsPayload, updateUserPreferencesPayload } from "../../actions";
import * as api from "../../api";
import { UserInfoStoreError, UserInfoStoreSuccess } from "../../enums/store-messages/user-info";
import { NotificationGroup, NotificationPreferences, UserInfo, UserPreferences } from "../../reducers/states/user-info";
import { IsResponseValidUtil } from "../../utils/api-utils";
import { MapLayers } from "../../enums/map";
import { AgencyAbbr, AgencyIds, getAgencyId, JogId } from "../../reducers/states/agency";
import { AgencyFilterConversionMap, WspDistrictFilterKey } from "../../reducers/states/agency/wsp";
import { JogIdConversionMap } from "../../reducers/states/agency/utils";

// Old VCC Defaults, before saved preferences were created
export const DefaultUserPreferences = {
  isMapCenter: true,
  isLeftPanelCollapsed: false,
  isRightPanelCollapsed: false,
  mapLayers: [MapLayers.CAMERA_LAYER, MapLayers.TRAFFIC_LAYER, MapLayers.CONSTRUCTION_EVENTS_DATA],
  markerLayers: [MapLayers.DISPATCH_EVENTS, MapLayers.VCC_INCIDENTS],
  agencyFilters: [AgencyIds.KingCountyMetro, AgencyIds.SeattlePD, AgencyIds.SeattleFD, WspDistrictFilterKey.SEA_D2],
  jogFilters: [JogId.SeattleArea],
  timeFrame: 30,
  feedView: "All",
  showOpenDispatchesOnly: true,

  jogNotificationsPostConf: [] as string[],
}

export function* loadUserInfoAndPreferences(action: {
  type: typeof types.GET_CURRENT_USER_INFO_AND_PREFERENCES 
}): Generator<any, void, any> {
  const session: any = yield Auth.currentSession();
  const email = session.getIdToken().payload["custom:vccEmail"] as string;
  
  // Wait for both calls to finsh.
  const [infoResult, prefsResult] = yield all([
    call(api.getUserInfo, email),
    call(api.getUserPreferences, email),
  ]);  

  // Control the put order to control the reducer order.
  // The reducer for prefs depends on userInfo already being in state.
  yield put(handleCurrentUserInfoResponse(infoResult));
  yield put(handleGetUserPreferencesResult(prefsResult));
}
function* watchLoadUserInfoAndPreferences() {
  yield takeEvery(actions.types.GET_CURRENT_USER_INFO_AND_PREFERENCES, loadUserInfoAndPreferences);
}

export function* getCurrentUserInfo(action: {
  type: typeof types.GET_CURRENT_USER_INFO;
  payload: {};
}): Generator<any, void, unknown> {
  try {
    const session: any = yield Auth.currentSession();
    const email = session.getIdToken().payload["custom:vccEmail"] as string;
    const result: any = yield call(api.getUserInfo, email);
    
    yield put(handleCurrentUserInfoResponse(result));
  } catch (e) {
    yield put(actions.getCurrentUserInfoFailure(UserInfoStoreError.GET_CURRENT_USER_INFO));
  }
}
function handleCurrentUserInfoResponse(result: any) {
  try {
    if (result && result.status === 200 && IsResponseValidUtil(result.data)) {
      
      // Default the current user jogId to Seattle if they don't already have one set.
      // We only need to do this for loading the current user, so a default map view will load.
      // Not for the other the general "getuserInfo" function below
      let userInfo = result.data as UserInfo;
      userInfo.jogId = userInfo.jogId || JogId.SeattleArea;

      return actions.getCurrentUserInfoSuccess(userInfo);
    } else {      
      return actions.getCurrentUserInfoFailure(UserInfoStoreError.GET_CURRENT_USER_INFO);
    }
  } catch (e) {
    return actions.getCurrentUserInfoFailure(UserInfoStoreError.GET_CURRENT_USER_INFO);
  }
}
function* watchGetCurrentUserInfoEvent() {
  yield takeEvery(actions.types.GET_CURRENT_USER_INFO, getCurrentUserInfo);
}

export function* getUserInfo(action: any): Generator<any, void, unknown> {
  try {
    const result: any = yield call(api.getUserInfo, action.payload.email);
    if (result && result.status === 200 && IsResponseValidUtil(result.data)) {
      yield put(actions.getUserInfoSuccess(result.data as UserInfo));
    } else {
      yield put(actions.getUserInfoFailure(UserInfoStoreError.GET_USER_INFO));
    }
  } catch (e) {
    yield put(actions.getUserInfoFailure(UserInfoStoreError.GET_USER_INFO));
  }
}
function* watchGetUserInfoEvent() {
  yield takeEvery(actions.types.GET_USER_INFO, getUserInfo);
}

export function* getAllNotificationGroups(): Generator<any, void, unknown> {
  try {
    const result: any = yield call(api.getNotificationGroups);
    if (result && result.status === 200) {
      if (IsResponseValidUtil(result.data)) {
        let apiResultPayload: NotificationGroup[] = result.data;
        yield put(actions.getAllNotificationGroupsSuccess(apiResultPayload));
      } else {
        yield put(actions.getAllNotificationGroupsFailure(UserInfoStoreError.GET_ALL_NOTIFICATION_GROUPS));
      }
    } else {
      yield put(actions.getAllNotificationGroupsFailure(UserInfoStoreError.GET_ALL_NOTIFICATION_GROUPS));
    }
  } catch (e) {
    yield put(actions.getAllNotificationGroupsFailure(UserInfoStoreError.GET_ALL_NOTIFICATION_GROUPS));
  }
}
function* watchGetAllNotificationGroups() {
  yield takeEvery(actions.types.GET_ALL_NOTIFICATION_GROUPS, getAllNotificationGroups);
}

export function* getUserNotificationPrefs(action: {
  type: typeof types.GET_USER_NOTIFICATION_PREFS;
  payload: actions.getUserNotificationPrefsPayload;
}): Generator<any, void, unknown> {
  try {
    const result: any = yield call(api.getUserNotificationPreferences, action.payload.email);
    if (result && result.status === 200) {
      if (IsResponseValidUtil(result.data)) {
        let apiResultPayload: NotificationPreferences = result.data;
        
        yield put(actions.getUserNotificationPrefsSuccess(apiResultPayload));
      } else {
        yield put(actions.getUserNotificationPrefsFailure(UserInfoStoreError.GET_USER_NOTIFICATION_PREFS));
      }
    } else {
      yield put(actions.getUserNotificationPrefsFailure(UserInfoStoreError.GET_USER_NOTIFICATION_PREFS));
    }
  } catch (e) {
    // Future: Implement proper logging and error handling
    console.log(e);
  }
}
function* watchGetUserNotificationPrefs() {
  yield takeEvery(actions.types.GET_USER_NOTIFICATION_PREFS, getUserNotificationPrefs);
}

export function* updateUserNotificationPrefs(action: {
  type: typeof types.UPDATE_USER_NOTIFICATION_PREFS;
  payload: updateUserNotificationPrefsPayload;
}): Generator<any, void, unknown> {
  try {
    const result: any = yield call(api.updateUserNotificationPreferences, action.payload);
    if (result && result.status === 200) {
      if (IsResponseValidUtil(result.data)) {
        yield put(actions.updateUserNotificationPrefsSuccess(UserInfoStoreSuccess.UPDATE_USER_NOTIFICATION_PREFS));
      } else {
        yield put(actions.updateUserNotificationPrefsFailure(UserInfoStoreError.UPDATE_USER_NOTIFICATION_PREFS));
      }
    } else {
      yield put(actions.updateUserNotificationPrefsFailure(UserInfoStoreError.UPDATE_USER_NOTIFICATION_PREFS));
    }
  } catch (e) {
    // Future: Implement proper logging and error handling
    console.log(e);
  }
}
function* watchUpdateUserNotificationPrefs() {
  yield takeEvery(actions.types.UPDATE_USER_NOTIFICATION_PREFS, updateUserNotificationPrefs);
}



export function* updateUserProfile(action: {
  type: typeof types.UPDATE_USER_PROFILE;
  payload: updateUserProfilePayload;
}): Generator<any, void, unknown> {
  try {
    const session: any = yield Auth.currentSession();
    const cognitoUsername = session.getAccessToken().payload["username"];
    const result: any = yield call(
      api.updateUserProfile,
      cognitoUsername,
      action.payload.givenName,
      action.payload.familyName,
      action.payload.phoneNumber,
    );
    if (result && result.status === 200) {
      if (IsResponseValidUtil(result.data)) {
        yield call(actions.getAllUserInfoRequest);
        yield put(actions.updateUserProfileSuccess(UserInfoStoreSuccess.UPDATE_USER_PROFILE));
      } else {
        yield put(actions.updateUserProfileFailure(UserInfoStoreError.UPDATE_USER_PROFILE));
      }
    } else {
      yield put(actions.updateUserProfileFailure(UserInfoStoreError.UPDATE_USER_PROFILE));
    }
  } catch (e) {
    console.log(e);
  }
}
function* watchUpdateUserProfile() {
  yield takeEvery(actions.types.UPDATE_USER_PROFILE, updateUserProfile);
}

export function* getUserPreferences(action: {
  type: typeof types.GET_USER_PREFERENCES;
  payload: {};
}): Generator<any, void, unknown> {
  console.log("getUserPreferences");
  try {
    const session: any = yield Auth.currentSession();
    const email = session.getIdToken().payload["custom:vccEmail"] as string;
    const result: any = yield call(api.getUserPreferences, email);
    
    yield put(handleGetUserPreferencesResult(result));
  } catch (e) {
    yield put(actions.getUserPreferencesFailure(UserInfoStoreError.GET_USER_PREFERENCES));
  }  
}
function handleGetUserPreferencesResult(result: any) {
  try {
    if (result && result.status === 200) {
      let apiResultPayload: UserPreferences = result.data;

      if (apiResultPayload.isMapCenter === undefined) {
        apiResultPayload = DefaultUserPreferences
      } else {
        if (!apiResultPayload?.jogFilters?.length) {
          apiResultPayload.jogFilters = []
        }

        // This field is is only used during user confirmation, so we want to delete it from the DynamoDB item after that,
        // which will happen by this delete if/when prefs are updated after this get
        if(apiResultPayload.jogNotificationsPostConf) {
          delete apiResultPayload.jogNotificationsPostConf;
        }

        // These conversions must remain unless a DynamoDB data migration is done to make sure 
        // there are no instances of the old values.

        // convert obsolete agency filter values
        //   Although the endpoint returns filter keys like "SPD" and "WSP: D2", they will be converted 
        //   and the app state will have, and code will reference, values like "3" and "2: D2" instead
        const mappedFilters = apiResultPayload.agencyFilters.map(f => (AgencyFilterConversionMap.get(f) || getAgencyId(f)).replace(AgencyAbbr.WSP, AgencyIds.WSP));

        // use a Set to eliminate duplicatse. Which only happens when going back and forth between "old code" in dev and "new code" on local.
        // todo: Delete this and test before or after Agency Admin release.
        apiResultPayload.agencyFilters = [...(new Set(mappedFilters)).values()];
        
        // convert obsolete JOG filter values
        //   although older saved preferences may have values like like "1" and "2",
        //   they will be converted to zero padded 2 character values like "01" and "02"
        apiResultPayload.jogFilters = apiResultPayload.jogFilters.map(id => JogIdConversionMap.get(id) || id);

        // todo: jcavner: Move code from reducer to here for setting "defualt prefs"
      }

      return actions.getUserPreferencesSuccess(apiResultPayload);
    } else {
      return actions.getUserPreferencesFailure(UserInfoStoreError.GET_USER_PREFERENCES);
    }
  } catch (e) {
    // Future: Implement proper logging and error handling
    return actions.getUserPreferencesFailure(UserInfoStoreError.GET_USER_PREFERENCES);
  }
}
function* watchGetUserPreferences() {
  yield takeEvery(actions.types.GET_USER_PREFERENCES, getUserPreferences);
}

export function* updateUserPreferences(action: {
  type: typeof types.UPDATE_USER_PREFERENCES;
  payload: updateUserPreferencesPayload;
}): Generator<any, void, unknown> {
  try {
    const session: any = yield Auth.currentSession();
    const email = action.payload.userEmail ?? session.getIdToken().payload["custom:vccEmail"];
    let prefs: UserPreferences = action.payload.userPreferences;
    const result: any = yield call(api.updateUserPreferences,
      email,
      prefs.isMapCenter,
      prefs.isLeftPanelCollapsed,
      prefs.isRightPanelCollapsed,
      prefs.agencyFilters,
      prefs.jogFilters,
      prefs.mapLayers,
      prefs.markerLayers,
      prefs.timeFrame,
      prefs.feedView,
      prefs.showOpenDispatchesOnly,
      prefs.jogNotificationsPostConf,
    );
    if (result && result.status === 200) {
      yield put(actions.updateUserPreferencesSuccess(result.data));
    } else {
      yield put(actions.updateUserPreferencesFailure(UserInfoStoreError.UPDATE_USER_PREFERENCES));
    }
  } catch (e) {
    // Future: Implement proper logging and error handliang
    yield put(actions.updateUserPreferencesFailure(UserInfoStoreError.UPDATE_USER_PREFERENCES));
  }
}
function* watchUpdateUserPreferences() {
  yield takeEvery(actions.types.UPDATE_USER_PREFERENCES, updateUserPreferences);
}
const userInfoSagas = [
  fork(watchLoadUserInfoAndPreferences),
  fork(watchGetCurrentUserInfoEvent),
  fork(watchGetUserInfoEvent),
  fork(watchGetAllNotificationGroups),
  fork(watchGetUserNotificationPrefs),
  fork(watchUpdateUserNotificationPrefs),
  fork(watchUpdateUserProfile),
  fork(watchGetUserPreferences),
  fork(watchUpdateUserPreferences),
];

export default userInfoSagas;
